/**
 * Lifetime counters — the only progression state that is ever stored.
 *
 * They only go up. Nothing in a game should ever decrement one: a counter
 * that can fall would let a level or a completed challenge be taken back, and
 * the rule everywhere in this project is that progress once earned is never
 * retracted. Harvesting a tree does not reduce `matured`; it increments
 * `harvested`.
 */

export type Counters<M extends string = string> = Readonly<Partial<Record<M, number>>>;

/** A counter's value, with anything missing, negative or non-finite read as 0. */
export function read<M extends string>(counters: Counters<M>, metric: M): number {
  const value = counters[metric];
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

/** Add to a counter in place. Negative amounts are ignored, never applied. */
export function bump<M extends string>(
  counters: Partial<Record<M, number>>,
  metric: M,
  by = 1,
): void {
  if (!(by > 0) || !Number.isFinite(by)) return;
  counters[metric] = read(counters, metric) + Math.floor(by);
}

/**
 * Clean a counters object read back from storage.
 *
 * localStorage is user-editable and survives across builds, so anything could
 * be in there: strings, `NaN`, a prototype key. Only own, finite,
 * non-negative numbers under a plain key survive.
 */
export function sanitiseCounters(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [key, value] of Object.entries(raw)) {
    if (!/^[a-zA-Z][a-zA-Z0-9_-]{0,31}$/.test(key)) continue;
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) continue;
    out[key] = Math.floor(value);
  }
  return out;
}
